import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react'
import { InteractionTag, Text } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { getTemplateInterval } from '../../components/Form/Finance/Invoice/InvoiceFile'
import { InvoiceApprovalMutationDto, UserInvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'
import BaseTravelOverview, { BaseTravelOverviewProps, ExtendedTravelOverviewDto, TravelFilterType } from '../Travel/BaseTravelOverview'

export type ViewInvoiceApprovalTravelsProps = ViewInvoiceApprovalComponentProps & {
    setTravelsCount: Dispatch<SetStateAction<number>>
    setTravels: Dispatch<SetStateAction<ExtendedTravelOverviewDto[]>>
    selectedTravels: ExtendedTravelOverviewDto[]
}

export function TravelsHeaderFilterTags(props: BaseTravelOverviewProps & { userApprovals: UserInvoiceApprovalMutationDto[] }) {
    const { values, setValues } = useFormikContext<TravelFilterType>()
    const overviewStyles = useOverviewStyles()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {props.userApprovals?.map((item, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === item.userId}
                    onClick={() => setValues({ ...props.defaultFilterValues, users: [item.userId], tag: item.userId })}
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <PersonAvatar name={getUserById(item.userId)?.fullName} color={'colorful'} size={24} />
                </InteractionTag>
            ))}
        </div>
    )
}

export default function ViewInvoiceApprovalTravels(props: ViewInvoiceApprovalTravelsProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const defaultFilterValues = useMemo<TravelFilterType>(
        () => ({
            from: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).start.toISODate(),
            to: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).end.toISODate(),
            users: [],
            isBilled: props.approvalStatus === InvoiceApprovalStatus.Done,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            invoiceApprovals: values.id ? [values.id] : [],
            attributes: [],
            query: '',
        }),
        [values, props.invoiceTemplate],
    )

    const fixedFilterValues = useMemo<TravelFilterType>(
        () => ({
            files: values.fileIds || [],
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values, props.invoiceTemplate],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseTravelOverview
                    baseUrl={`/invoice-approval/${props.approval?.id}/travel`}
                    hideFileField
                    hideInvoiceField
                    focusQueryOnRender={false}
                    renderLink={!!values.id}
                    headerDefaultFilterTags={filterProps => (
                        <TravelsHeaderFilterTags
                            {...(filterProps as BaseTravelOverviewProps)}
                            baseUrl={`/invoice-approval/${props.approval?.id}/travel`}
                            userApprovals={values.userApprovals}
                        />
                    )}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(column, result) => props.setTravelsCount(result.total)}
                    onLoadItems={(column, columnPages, items) => props.setTravels([...items])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedTravels.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedTravels.includes(item)).length === 0}
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('travels', [...values.travels, item.id])
                            if (values.id) {
                                axios.post<UserInvoiceApprovalDto[]>(`/finance/invoice-approval/${values.id}/travel?id=${item.id}`).then(response => {
                                    if (!!response?.data) {
                                        setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                    }
                                })
                            }
                        } else {
                            setFieldValue('travels', [...values.travels.filter(travel => travel !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/travel?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'travels',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios
                                    .post<
                                        UserInvoiceApprovalDto[]
                                    >(`/finance/invoice-approval/${values.id}/travel?id=${items.map(item => item.id).join(',')}`)
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('travels', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/travel?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedTravels}
                />
            )}
        </div>
    )
}
